import React from 'react';
import { useNavigate } from 'react-router-dom';
import { PageHeader } from '../components/Shared/PageHeader';
import { useTranslation } from 'react-i18next';
import { usePageTitle } from '../hooks/usePageTitle';
import {
  useGetIstiftaListQuery,
  useGetIstiftaByStatusQuery,
  useUpdateIstiftaStatusMutation,
} from '../redux/api/istiftaApi';
import { ISupportTicket } from '../common/interfaces/TechnicalSupport.interface';
import { Button, Select, Table } from 'flowbite-react';
import { HiInbox } from 'react-icons/hi';
import { CustomPagination } from '../components/Shared/CustomPagination/CustomPagination';
import { TableCell, TableCommon } from '../components/Table/Table';
import { StatusBadge } from '../components/Shared/StatusBadge/StatusBadge';
import type { StatusBadgeVariant } from '../components/Shared/StatusBadge/StatusBadge';
import { SupportStatus } from '../common/enums/SupportStatus';
import { Loading } from '../components/Shared/Loading/Loading';
import { ErrorComponent } from '../components/Shared/ErrorComponent/ErrorComponent';
import { EmptyState } from '../components/Shared/EmptyState/EmptyState';
import { useAppSelector } from '../redux/hooks/useAppSelector';
import { selectIsRTL } from '../redux/selectors/displaySettingsSelectors';
import { selectIsDarkMode } from '../redux/selectors/displaySettingsSelectors';
import { usePagination } from '../hooks/usePagination';
import { truncateText } from '../utils/truncateText';
import { IstiftaFilters } from '../common/interfaces/Istifta';

const STATUS_VARIANT: Record<SupportStatus, StatusBadgeVariant> = {
  [SupportStatus.NEW]: 'info',
  [SupportStatus.IN_PROGRESS]: 'warning',
  [SupportStatus.FIXED]: 'success',
  [SupportStatus.CANCELLED]: 'error',
};

const IstiftaPage: React.FC = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const isRtl = useAppSelector(selectIsRTL);
  const isDarkMode = useAppSelector(selectIsDarkMode);
  const buttonClassName = `btn ${isDarkMode ? 'btn-dark' : 'btn-light'}`;

  const [filters, setFilters] = React.useState<IstiftaFilters>({
    status: 'ALL',
    pageSize: 10,
    page: 1,
  });
  const statusFilter = filters.status;
  const { currentPage, setCurrentPage, size, setSize } = usePagination(
    filters.pageSize
  );

  usePageTitle(t('sidebar.istifta'));

  const listResult = useGetIstiftaListQuery(
    { page: currentPage, size },
    {
      skip: statusFilter !== 'ALL',
      refetchOnMountOrArgChange: true,
      pollingInterval: 15000,
    }
  );
  const byStatusResult = useGetIstiftaByStatusQuery(
    { status: statusFilter as SupportStatus, page: currentPage, size },
    {
      skip: statusFilter === 'ALL',
      refetchOnMountOrArgChange: true,
      pollingInterval: 15000,
    }
  );

  const [updateIstiftaStatus] = useUpdateIstiftaStatusMutation();

  const activeResult = statusFilter === 'ALL' ? listResult : byStatusResult;
  const { data: response, isLoading, isError, error, refetch } = activeResult;

  const tickets: ISupportTicket[] = response?.data?.items ?? [];
  const totalPages = response?.data?.totalPages ?? 1;

  const handleStatusChange = async (id: string, status: SupportStatus) => {
    try {
      await updateIstiftaStatus({ id, status }).unwrap();
    } catch {
      // status reverts via RTK cache
    }
  };

  const columnHeadLabels = [
    { id: 'ticketNumber', label: t('istifta.ticketNumber') },
    { id: 'message', label: t('istifta.message') },
    { id: 'submittedAt', label: t('istifta.submittedAt') },
    { id: 'status', label: t('istifta.status') },
    { id: 'preview', label: t('istifta.preview') },
  ];

  if (isLoading) return <Loading />;
  if (isError) return <ErrorComponent error={error} onRetry={refetch} />;

  return (
    <>
      <PageHeader title={t('sidebar.istifta')} />

      <div className="mb-4 flex flex-wrap items-center justify-end gap-2">
        <Select
          className="min-w-[160px]"
          value={statusFilter}
          onChange={(e) => {
            setFilters((f) => ({
              ...f,
              status: e.target.value as IstiftaFilters['status'],
            }));
            setCurrentPage(1);
          }}
        >
          <option value="ALL">{t('istifta.allStatuses')}</option>
          {Object.values(SupportStatus).map((s) => (
            <option key={s} value={s}>
              {t('technicalSupport.statusList.' + s.toLowerCase())}
            </option>
          ))}
        </Select>
        <Select
          className="w-[90px]"
          value={size}
          onChange={(e) => {
            const ps = Number(e.target.value) as IstiftaFilters['pageSize'];
            setFilters((f) => ({ ...f, pageSize: ps }));
            setSize(ps);
            setCurrentPage(1);
          }}
        >
          {[10, 25, 50].map((n) => (
            <option key={n} value={n}>
              {n}
            </option>
          ))}
        </Select>
      </div>

      {tickets.length === 0 ? (
        <EmptyState
          Icon={HiInbox}
          title={t('istifta.allStatuses')}
          hint={t('istifta.emptyHint')}
        />
      ) : (
        <>
          <TableCommon columns={columnHeadLabels}>
            {tickets.map((ticket, index) => (
              <Table.Row key={index} className="bg-white">
                <TableCell>{ticket.ticketNumber}</TableCell>
                <TableCell>
                  <span title={ticket.message}>
                    {truncateText(
                      ticket.message,
                      60,
                      isRtl ? 'ar' : 'en',
                      isRtl
                    )}
                  </span>
                </TableCell>
                <TableCell>
                  {new Date(ticket.createdAt).toLocaleString(
                    isRtl ? 'ar' : 'en'
                  )}
                </TableCell>
                <TableCell>
                  <div className="relative inline-flex cursor-pointer">
                    <StatusBadge
                      variant={STATUS_VARIANT[ticket.status] ?? 'neutral'}
                      size="sm"
                    >
                      {t(
                        'technicalSupport.statusList.' +
                          ticket.status.toLowerCase()
                      )}
                    </StatusBadge>
                    <select
                      value={ticket.status}
                      className="absolute inset-0 cursor-pointer opacity-0"
                      onChange={(e) =>
                        handleStatusChange(
                          ticket.id,
                          e.target.value as SupportStatus
                        )
                      }
                    >
                      {Object.values(SupportStatus).map((status) => (
                        <option key={status} value={status}>
                          {t(
                            'technicalSupport.statusList.' +
                              status.toLowerCase()
                          )}
                        </option>
                      ))}
                    </select>
                  </div>
                </TableCell>
                <TableCell>
                  <Button
                    size="xs"
                    className={buttonClassName}
                    onClick={() => navigate(`/istifta/${ticket.id}`)}
                  >
                    {t('istifta.preview')}
                  </Button>
                </TableCell>
              </Table.Row>
            ))}
          </TableCommon>

          <CustomPagination
            currentPage={currentPage}
            totalPages={totalPages}
            onPageChange={setCurrentPage}
          />
        </>
      )}
    </>
  );
};

export default IstiftaPage;
